import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppLayout from '../components/layout/AppLayout';
import { EventGroupGrid } from '../components/security/EventGroupGrid';
import {
  EventList,
  IpList,
  KpiCard,
  LoadingBoard,
  MonitorForm,
  MonitoredList,
  SecurityHeader,
  SecurityTabs,
} from '../components/security/SecurityLists';
import { BackLink } from '../components/security/SecurityDetail';
import { filterByProject, projectLabel, securityEventPath } from '../components/security/securityLabels';
import {
  apiCreateCybersecurityMonitored,
  apiGetCybersecurityEvents,
  apiGetCybersecurityIpCounters,
  apiGetCybersecurityMonitored,
  apiGetCybersecuritySummary,
  apiRevertCybersecurityMonitored,
} from '../api/client';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'events', label: 'Events' },
  { id: 'ips', label: 'Addresses' },
  { id: 'monitored', label: 'Watch list' },
];

export default function SecurityProjectPage() {
  const { source } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState(null);
  const [ips, setIps] = useState([]);
  const [events, setEvents] = useState([]);
  const [monitored, setMonitored] = useState([]);
  const [actorKey, setActorKey] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      const [sum, counters, ev, mon] = await Promise.all([
        apiGetCybersecuritySummary(),
        apiGetCybersecurityIpCounters(),
        apiGetCybersecurityEvents(),
        apiGetCybersecurityMonitored(),
      ]);
      const project = (sum?.sources || []).find((item) => item.source === source) || null;
      setSummary(project);
      setIps(filterByProject(counters?.rows || [], source));
      setEvents(filterByProject(ev?.rows || [], source));
      setMonitored(filterByProject(mon?.rows || [], source));
    } catch (e) {
      setError(e.message || 'Failed to load project security data');
    } finally {
      setLoading(false);
    }
  }, [source]);

  useEffect(() => {
    load();
  }, [load]);

  const onRevert = async (backend, id) => {
    try {
      await apiRevertCybersecurityMonitored(backend, id);
      await load();
    } catch (e) {
      setError(e.message || 'Could not return this person to normal');
    }
  };

  const onMonitor = async (event) => {
    event.preventDefault();
    if (!actorKey.trim()) return;
    setSaving(true);
    try {
      await apiCreateCybersecurityMonitored(source, {
        actor_key: actorKey.trim(),
        reason: 'admin',
      });
      setActorKey('');
      await load();
    } catch (e) {
      setError(e.message || 'Could not add this person to the watch list');
    } finally {
      setSaving(false);
    }
  };

  const openGroup = (id) => {
    setGroup(id);
    setTab('events');
  };

  const alertAt = summary?.ip_block_alert || 200;
  const name = projectLabel(source);
  const urgent = summary?.critical_unnotified || 0;
  const needsAttention = !!summary?.error || urgent > 0;

  return (
    <AppLayout>
      <div className="page-content sec-page">
        <BackLink />
        <div style={{ height: 12 }} />
        <SecurityHeader
          title={name}
          subtitle="Events, blocked addresses, and the watch list for this project."
          alert={needsAttention}
          statusText={summary?.error
            ? 'This project is offline'
            : urgent
              ? `${urgent} urgent item${urgent === 1 ? '' : 's'} need a look`
              : 'This project looks calm'}
          onRefresh={load}
        />

        {error && <div className="sec-alert">{error}</div>}

        <SecurityTabs tabs={TABS} active={tab} onChange={setTab} />

        {loading && <LoadingBoard />}

        {!loading && summary?.error && <div className="sec-alert">{summary.error}</div>}

        {!loading && (
          <>
            {tab === 'overview' && (
              <div className="animate-fadeIn">
                <div className="sec-kpi-grid">
                  <KpiCard
                    label="Urgent"
                    value={urgent}
                    danger={!!urgent}
                    hint="View events →"
                    onClick={() => setTab('events')}
                  />
                  <KpiCard
                    label="Addresses seen"
                    value={summary?.ip_counter_total || ips.length}
                    hint="Investigate addresses →"
                    onClick={() => setTab('ips')}
                  />
                  <KpiCard
                    label="On the watch list"
                    value={summary?.monitored_active || 0}
                    hint="Open watch list →"
                    onClick={() => setTab('monitored')}
                  />
                  <KpiCard label="Block threshold" value={alertAt} />
                </div>
                <h2 className="sec-section-title">Events by group</h2>
                <EventGroupGrid events={events} onSelectGroup={openGroup} activeGroup={group} />
              </div>
            )}

            {tab === 'events' && (
              <EventList
                events={events}
                group={group}
                onFilter={setGroup}
                onOpen={(row) => {
                  const path = securityEventPath(row);
                  if (path) navigate(path);
                }}
              />
            )}

            {tab === 'ips' && (
              <IpList
                ips={ips}
                group={group}
                alertAt={alertAt}
                onFilter={setGroup}
                onOpen={(row) => navigate(`/cybersecurity/ip/${row.backend}/${encodeURIComponent(row.ip)}`)}
              />
            )}

            {tab === 'monitored' && (
              <div>
                <MonitorForm
                  actorKey={actorKey}
                  setActorKey={setActorKey}
                  monitorSource={source}
                  setMonitorSource={() => {}}
                  sources={[{ source }]}
                  saving={saving}
                  onSubmit={onMonitor}
                  lockSource
                />
                <MonitoredList
                  rows={monitored}
                  onOpen={(row) => navigate(`/cybersecurity/monitored/${row.backend}/${row.id}`)}
                  onRevert={onRevert}
                />
              </div>
            )}
          </>
        )}
      </div>
    </AppLayout>
  );
}
